import { navigate } from 'gatsby';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-use';

import { PAGE_SIZE_OPTIONS } from '@components/DataTable/MuiDataTable/options';

const DEFAULT_PAGE_SIZE = 25;

/**
 * Syncs MuiDataTable page, page size, and (optionally) search text with the
 * URL so the user's position is preserved on back navigation and page refresh.
 *
 * Must be explicitly opted into by passing pageParam to MuiDataTable.
 * Tables that manage their own pagination (serverSide, embedded, modal) should
 * not pass pageParam and will be unaffected.
 *
 * @param pageParam - The URL param prefix, e.g. 'page' gives:
 *   ?page=2&pageSize=50&pageSearch=john  (page is 1-based: ?page=2 = second page)
 *
 *   For multi-table pages pass a unique key per table, e.g. pageParam='discern':
 *   ?discern=2&discernSize=50&discernSearch=john
 */
export function useTablePagination(pageParam: string) {
  const location = useLocation();
  const params = new URLSearchParams(location.search ?? '');

  const sizeKey = `${pageParam}Size`;
  const searchKey = `${pageParam}Search`;

  // URL stores 1-based page numbers (?page=2 = second page).
  // Convert back to 0-based index for mui-datatables.
  const rawPage = parseInt(params.get(pageParam) ?? '0', 10);
  const initialPage = rawPage > 1 ? rawPage - 1 : 0;

  const rawSize = parseInt(
    params.get(sizeKey) ?? String(DEFAULT_PAGE_SIZE),
    10
  );
  const initialRowsPerPage = (PAGE_SIZE_OPTIONS as readonly number[]).includes(
    rawSize
  )
    ? rawSize
    : DEFAULT_PAGE_SIZE;

  const initialSearchText = params.get(searchKey) ?? '';

  function updateUrl(updates: Record<string, string | null>, replace = false) {
    const next = new URLSearchParams(location.search ?? '');
    for (const [key, value] of Object.entries(updates)) {
      if (!value) {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    }
    const qs = next.toString();
    navigate(qs ? `${location.pathname}?${qs}` : (location.pathname ?? ''), {
      replace,
    });
  }

  function setPage(newPage: number, replace = false) {
    // Convert 0-based mui-datatables index to 1-based URL param.
    updateUrl(
      { [pageParam]: newPage > 0 ? String(newPage + 1) : null },
      replace
    );
  }

  function setRowsPerPage(newSize: number) {
    updateUrl({
      [sizeKey]: newSize !== DEFAULT_PAGE_SIZE ? String(newSize) : null,
      [pageParam]: null,
    });
  }

  const searchDebounceTimer = useRef<ReturnType<typeof setTimeout>>();
  function setSearchText(text: string) {
    clearTimeout(searchDebounceTimer.current);
    searchDebounceTimer.current = setTimeout(() => {
      updateUrl({ [searchKey]: text || null, [pageParam]: null });
    }, 300);
  }

  useEffect(() => {
    return () => clearTimeout(searchDebounceTimer.current);
  }, []);

  return {
    initialPage,
    initialRowsPerPage,
    initialSearchText,
    setPage,
    setRowsPerPage,
    setSearchText,
  };
}
